/**
 * Team-Chat module — 1:1 port of the prototype `isTeamchat` content block
 * (Hausverwaltung Hero.dc.html L1200–1243, `teamMessages` data L2674–2680).
 * The sidebar lives in `module-teamchat-sidebar.tsx`, static data/types in
 * `module-teamchat-data.ts`. A group-DM thread (Carla Vogt, David Lehmann) with
 * a header avatar stack and a composer. All rows are inert — the module never
 * calls `setActive` / `setInCall`.
 */
import { ld } from "@/lib/theme/light-dark";
import {
  ArrowUp,
  ImageIcon,
  Paperclip,
  Phone,
  Smile,
  Video,
} from "lucide-react";
import type { ReactNode } from "react";

import { useTranslations } from "next-intl";
import { H, MODULE_HEIGHT } from "../../hero-tokens";
import type { ModuleProps } from "../module-registry";
import type { TeamMessage } from "./module-teamchat-data";
import { teamMessages } from "./module-teamchat-data";
import { TeamchatSidebar } from "./module-teamchat-sidebar";

function ThreadHeaderAvatar(props: {
  readonly initials: string;
  readonly bg: string;
  readonly fg: string;
  readonly first?: boolean;
}): ReactNode {
  return (
    <span
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: props.bg,
        color: props.fg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 11,
        fontWeight: 600,
        border: `2px solid ${ld("#fff", "#181b1f")}`,
        marginLeft: props.first === true ? 0 : -10,
      }}
    >
      {props.initials}
    </span>
  );
}

function ThreadHeaderButton(props: {
  readonly children: ReactNode;
}): ReactNode {
  return (
    <span
      style={{
        width: 34,
        height: 34,
        borderRadius: 9,
        border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: ld("rgba(11,15,25,0.55)", "rgba(250,250,250,0.55)"),
        cursor: "pointer",
      }}
    >
      {props.children}
    </span>
  );
}

function MessageRow(props: { readonly entry: TeamMessage }): ReactNode {
  const { entry } = props;
  return (
    <div style={{ display: "flex", gap: 11, alignItems: "flex-start" }}>
      <span style={{ position: "relative", flexShrink: 0 }}>
        <span
          style={{
            width: 38,
            height: 38,
            borderRadius: "50%",
            background: entry.avBg,
            color: entry.avFg,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {entry.avInit}
        </span>
        <span
          style={{
            position: "absolute",
            right: -1,
            bottom: -1,
            width: 11,
            height: 11,
            borderRadius: "50%",
            background: entry.statusDot,
            border: `2px solid ${ld("#fff", "#181b1f")}`,
          }}
        />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: H.ink }}>
            {entry.author}
          </span>
          <span style={{ fontSize: 11, color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>
            {entry.time}
          </span>
        </div>
        <div
          style={{
            fontSize: 13.5,
            color: ld("rgba(11,15,25,0.8)", "rgba(250,250,250,0.8)"),
            lineHeight: 1.5,
            marginTop: 2,
          }}
        >
          {entry.text}
        </div>
      </div>
    </div>
  );
}

function ThreadHeader(): ReactNode {
  const t = useTranslations("marketing.moduleTeamchat");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        paddingBottom: 13,
        borderBottom: `1px solid ${ld("#F1F2F4", "#2b2e32")}`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <ThreadHeaderAvatar initials="CV" bg={ld("#EEE9F7", "#35303c")} fg={ld("#6D5BA6", "#ae9ded")} first />
          <ThreadHeaderAvatar initials="DL" bg={ld("#E7EEF8", "#2c323a")} fg={ld("#3F6CB0", "#7cacf5")} />
          <ThreadHeaderAvatar initials="NH" bg={ld("#0B0F19", "#fafafa")} fg={ld("#fff", "#181b1f")} />
        </div>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>
            {t("carlaVogtDavidLehmann")}
          </span>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11.5,
              color: H.inkDim,
              marginTop: 1,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: "#3A9E6B",
              }}
            />
            {t("aktiv")}
          </span>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ThreadHeaderButton>
          <Phone width={16} height={16} strokeWidth={1.8} />
        </ThreadHeaderButton>
        <ThreadHeaderButton>
          <Video width={16} height={16} strokeWidth={1.8} />
        </ThreadHeaderButton>
      </div>
    </div>
  );
}

function Composer(): ReactNode {
  const t = useTranslations("marketing.moduleTeamchat");
  return (
    <div style={{ paddingTop: 12 }}>
      <div
        style={{
          border: `1px solid ${ld("#E7E9ED", "#32363a")}`,
          borderRadius: 12,
          padding: "11px 14px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        <span style={{ fontSize: 13.5, color: ld("rgba(11,15,25,0.4)", "rgba(250,250,250,0.4)") }}>
          {t("nachrichtCarlaVogtDavid")}
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              color: H.inkDim,
            }}
          >
            <Paperclip width={17} height={17} strokeWidth={1.8} />
            <ImageIcon width={17} height={17} strokeWidth={1.8} />
            <Smile width={17} height={17} strokeWidth={1.8} />
          </div>
          <span
            style={{
              width: 32,
              height: 32,
              borderRadius: 9,
              background: H.accent,
              color: ld("#fff", "#181b1f"),
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <ArrowUp width={16} height={16} strokeWidth={2} />
          </span>
        </div>
      </div>
    </div>
  );
}

function Content(): ReactNode {
  const tData = useTranslations("marketing.moduleTeamchatData");
  return (
    <div
      style={{
        flex: 1,
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minWidth: 0,
      }}
    >
      <ThreadHeader />
      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          padding: "18px 2px 0",
          display: "flex",
          flexDirection: "column",
          gap: 15,
        }}
      >
        {teamMessages(tData).map((entry, idx) =>
          entry.kind === "date" ? (
            <div
              key={`d-${entry.date}`}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: H.inkDim,
                  background: ld("#F4F5F7", "#272a2e"),
                  borderRadius: 999,
                  padding: "4px 13px",
                }}
              >
                {entry.date}
              </span>
            </div>
          ) : (
            <MessageRow key={`m-${entry.author}-${idx}`} entry={entry} />
          ),
        )}
      </div>
      <Composer />
    </div>
  );
}

/** Team-Chat module interior: 220px sidebar + flex thread content. */
export function ModuleTeamchat(_props: ModuleProps): ReactNode {
  return (
    <div
      style={{
        display: "flex",
        height: MODULE_HEIGHT,
        fontFamily: H.sans,
        color: H.ink,
      }}
    >
      <TeamchatSidebar />
      <Content />
    </div>
  );
}
